package com.evoapps.evoshift.domain.specialdays

import com.evoapps.evoshift.domain.legal.LegalCalendars
import com.evoapps.evoshift.domain.market.payroll.poland.PolishCalendar
import com.evoapps.evoshift.domain.model.DaySpecials
import com.evoapps.evoshift.domain.model.LocalEvent
import com.evoapps.evoshift.domain.model.SpecialDay
import com.evoapps.evoshift.domain.model.SpecialDayKind
import com.evoapps.evoshift.domain.model.Voivodeship
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus

/**
 * SH-227: what marks each day of a range: the events typed in (always) and, when [automatic],
 * the long weekends and, in Poland, the summer school holidays and [voivodeship]'s winter break
 * (none without a voivodeship). Days with nothing are left out of the map.
 */
object SpecialDaysCalendar {

    fun days(
        range: ClosedRange<LocalDate>,
        events: List<LocalEvent>,
        automatic: Boolean,
        voivodeship: Voivodeship?,
        poland: Boolean = true
    ): Map<LocalDate, DaySpecials> {
        if (range.endInclusive < range.start) return emptyMap()
        val byDate = mutableMapOf<LocalDate, MutableList<SpecialDay>>()
        fun add(date: LocalDate, day: SpecialDay) {
            if (date in range) byDate.getOrPut(date) { mutableListOf() } += day
        }
        events.sortedWith(compareBy<LocalEvent> { it.from.dayOfYearKey() }.thenBy { it.name.lowercase() }.thenBy { it.id })
            .forEach { event ->
                event.occurrencesIn(range).forEach { occurrence ->
                    occurrence.days().forEach { date ->
                        add(date, SpecialDay(SpecialDayKind.LOCAL_EVENT, occurrence.start, occurrence.endInclusive, name = event.name, eventId = event.id))
                    }
                }
            }
        if (automatic && poland) {
            LongWeekends.days(range).forEach { (date, day) -> add(date, day) }
            if (voivodeship != null) {
                SchoolBreaks.winterBreaks(voivodeship, range).forEach { period ->
                    period.days().forEach { add(it, SpecialDay(SpecialDayKind.WINTER_BREAK, period.start, period.endInclusive, voivodeship = voivodeship)) }
                }
            }
            SchoolBreaks.summerHolidays(range).forEach { period ->
                period.days().forEach { add(it, SpecialDay(SpecialDayKind.SUMMER_HOLIDAYS, period.start, period.endInclusive)) }
            }
        }
        // The marker's order: events first, then by kind (stable within a kind).
        return byDate.mapValues { (date, items) -> DaySpecials(date, items.sortedBy { it.kind.ordinal }) }
    }

    private fun LocalDate.dayOfYearKey(): Int = month.ordinal * 32 + day
}

/**
 * SH-227: Polish long weekends: a run of at least three days off (Saturdays, Sundays and public
 * holidays) with a holiday in it, where a single working day between a holiday and a day off (a
 * "bridge", taken off by many) counts as off. Holidays from the legal calendar (SH-55) for the
 * years it lists, else by the statute's rules ([PolishCalendar]).
 */
object LongWeekends {

    fun days(range: ClosedRange<LocalDate>): Map<LocalDate, SpecialDay> {
        if (range.endInclusive < range.start) return emptyMap()
        // A week either side, so a run across the range's edge keeps its whole length.
        val first = range.start.plus(DatePeriod(days = -7))
        val last = range.endInclusive.plus(DatePeriod(days = 7))
        val holidays = (first.year..last.year).flatMap { PolishCalendar.holidays(it) }.toSet()
        fun holiday(d: LocalDate) = d in holidays
        fun free(d: LocalDate) = d.dayOfWeek == DayOfWeek.SATURDAY || d.dayOfWeek == DayOfWeek.SUNDAY || holiday(d)
        fun bridge(d: LocalDate): Boolean {
            if (free(d)) return false
            val before = d.plus(DatePeriod(days = -1))
            val after = d.plus(DatePeriod(days = 1))
            return free(before) && free(after) && (holiday(before) || holiday(after))
        }
        val result = mutableMapOf<LocalDate, SpecialDay>()
        val run = mutableListOf<LocalDate>()
        fun close() {
            if (run.size >= 3 && run.any(::holiday)) {
                val name = run.firstNotNullOfOrNull(::holidayName)
                run.forEach { d ->
                    if (d in range) result[d] = SpecialDay(SpecialDayKind.LONG_WEEKEND, run.first(), run.last(), bridge = bridge(d), holidayName = name)
                }
            }
            run.clear()
        }
        var d = first
        while (d <= last) {
            if (free(d) || bridge(d)) run += d else close()
            d = d.plus(DatePeriod(days = 1))
        }
        close()
        return result
    }

    /** The holiday's name in the legal calendar (years it lists), or null. */
    private fun holidayName(date: LocalDate): String? =
        LegalCalendars.bundled("PL", date.year)?.holidays?.firstOrNull { it.localDate == date }?.name?.takeIf { it.isNotBlank() }
}

internal fun ClosedRange<LocalDate>.days(): List<LocalDate> {
    val out = mutableListOf<LocalDate>()
    var d = start
    while (d <= endInclusive) {
        out += d
        d = d.plus(DatePeriod(days = 1))
    }
    return out
}
